import { formatDate } from '@angular/common';
import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ControlsOf, FormGroup } from '@ngneat/reactive-forms';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Leave } from '../classes/leave';
import { Project } from '../classes/project.class';
import { RealisationHourtype } from '../classes/realisation';
import { User } from '../classes/user.class';
import {
    AddLeaveNameDialogComponent,
    returnData,
} from '../pages/employee-planning/add-leave-name-dialog/add-leave-name-dialog.component';
import { EmployeeDayInfoDialogComponent } from '../pages/employee-planning/employee-day-info-dialog/employee-day-info-dialog.component';
import { Settings } from '../settings.class';
import { TracklinePlanningDay } from '../trackline/pages/trackline-planning/trackline-planning-day';
import { Utils } from '../utils.class';
import { AuthorisationService } from './auth/authorisation.service';
import { ConfirmDialogService } from './confirm-dialog-service/confirm-dialog.service';
import { UserId } from './entities/entity-type.class';
import { LeaveService } from './leave.service';

export interface ProjectDay {
    project: Project;
    worknumber?: string;
    form: FormGroup<
        ControlsOf<{
            location: string;
            location_housenumber: string;
            location_place: string;
            location_postcode: string;
            location_straat: string;
            performer_id: number;
            projectmanager_id: number;
        }>
    >;
    days: {
        day: Date;
        project: Project;
    }[];
}

@Injectable({ providedIn: 'root' })
export class EmployeeService implements OnDestroy {
    private subscriptions = new Subscription();
    private environment = environment;

    constructor(
        private leaveService: LeaveService,
        private dialog: MatDialog,
        private confirmDialog: ConfirmDialogService,
        private router: Router
    ) {}

    getDefaultEmployees(employees: User[], entityEmployeeIds: number[], performerIds: number[]) {
        const fromEntity = employees.filter(e => entityEmployeeIds.includes(e.id)).map(e => e.id);

        const performerEmployeeIds = performerIds.flatMap(
            id => employees.find(e => e.id === id)?.employees?.map(e => e.id) ?? []
        );

        return employees.filter(
            e =>
                e.id !== UserId.HiringPerformer &&
                [...performerIds, ...fromEntity, ...performerEmployeeIds].includes(e.id)
        );
    }

    addLeave(employee: User, day: TracklinePlanningDay, fromDate: Date, toDate: Date) {
        this.subscriptions.add(
            this.leaveService
                .getFilteredList(fromDate, toDate)
                .pipe(take(1))
                .subscribe(leaves => {
                    const startOfDay = Utils.setTime(new Date(day.date), 0, 0);
                    const endOfDay = Utils.setTime(new Date(day.date), 23, 59);

                    let leave = this.leaveService.getByTypeAndDates(leaves, startOfDay, endOfDay);

                    if (!leave) {
                        const beginDate = new Date(day.date);

                        Utils.setTime(beginDate, Settings.DEFAULT_START, 0, 0);

                        const endDate = new Date(beginDate);

                        Utils.setTime(endDate, Settings.DEFAULT_START + Settings.DEFAULT_DURATION_SLEEP, 0, 0);

                        leave = new Leave();
                        leave.comment = 'onbeschikbaar weekplanning';
                        leave.begindate = beginDate;
                        leave.enddate = endDate;
                        leave.begintime = formatDate(leave.begindate, 'HH:mm', 'nl');
                        leave.endtime = formatDate(leave.enddate, 'HH:mm', 'nl');
                        leave.users = [];
                        leave.hourtype = RealisationHourtype.not_available;
                    }

                    const users = leave.users.map(u => u.id) ?? [];

                    if (users.includes(employee.id)) {
                        users.splice(users.indexOf(employee.id), 1);
                    } else {
                        users.push(employee.id);
                    }

                    leave.users = users as any;

                    this.leaveService.saveLeave(leave).subscribe();
                })
        );
    }

    showInfoDialog(user: User, day: TracklinePlanningDay) {
        const userId = user.id;

        const dateCompareFormat = (date: Date) => formatDate(date, 'yyyy-MM-dd', 'nl');

        const leaves = day.leaves
            .filter(l => l.users.map(u => u.id).includes(userId))
            .map(l => l.leave)
            .map(l => ({
                ...l,
                begindate: Utils.setTimeByString(new Date(l.begindate), l.begintime),
                enddate: Utils.setTimeByString(new Date(l.enddate), l.endtime),
            }));

        const dayDate = dateCompareFormat(day.date);
        const planningHas = [];
        const userPlanning = [];
        const projects = [];

        for (const item of day.items) {
            planningHas.push(...item.planning_has.filter(p => p.driver_user_id === userId));
            userPlanning.push(
                ...item.user_planning.filter(p => p.user_id === userId && dateCompareFormat(p.begindate) === dayDate)
            );
            projects.push({ project: { afas_project_id: item.afas_project_id } });
        }

        this.dialog.open(EmployeeDayInfoDialogComponent, {
            minWidth: '320px',
            data: {
                planningHas,
                userPlanning,
                leaves,
                projects,
                planningList: day.items,
                user,
                day: day.date,
                showLinks: false,
            },
        });
    }

    addOrRemoveLeave(
        user: User,
        day: Date,
        leaveData: Leave[],
        leavesToSave: Leave[],
        leaveDayUserMap: Map<string, Leave>,
        users: User[],
        projectDaysList: ProjectDay[],
        currentHourType: RealisationHourtype,
        contextProject: Project,
        contextComment: string,
        setHourType?: boolean,
        changeProject?: boolean,
        addComment = true,
        callback?: (leaveToSave: Leave[]) => void
    ) {
        const dateString = formatDate(day, 'yyyy-MM-dd', 'nl');
        const checkHourtype = leaveDayUserMap.get(this.makeKey(day, user.id))?.hourtype ?? currentHourType;
        const leaves = leaveData.filter(
            l =>
                l.hourtype === checkHourtype &&
                dateString === formatDate(l.begindate, 'yyyy-MM-dd', 'nl') &&
                !l.deleted_at
        );

        let leave = leaves[0];

        if (leaves.length > 1) {
            leave = leaves.find(l => !!l.users.find(u => u.id === user.id));
        }

        if (leave && leave?.interval_week) {
            this.confirmDialog
                .confirm({
                    title: 'Herhalend verlof',
                    text: `Herhalend verlof kan alleen via de verlofmodule worden aangepast.`,
                    cancel: 'Sluiten',
                    confirm: 'Naar verlof detail',
                })
                .then(
                    () => {},
                    () => {
                        this.router.navigateByUrl(`/leave/${leave.id}`);
                    }
                );
            return;
        }

        if (!leaveDayUserMap.has(this.makeKey(day, user.id)) && leave?.comment !== contextComment) {
            leave = null;
        }

        const leaveUsers = () => {
            if (!leave.users) {
                leave.users = [];
            }

            let actionIsAdd = false;

            if (leave.excluded.find(e => e.user_id === user.id)) {
                leave.excluded.splice(
                    leave.excluded.findIndex(e => e.user_id === user.id),
                    1
                );

                actionIsAdd = true;
            } else {
                if (leave?.common) {
                    leave.excluded.push({ user_id: user.id, date: day });

                    leaveDayUserMap.delete(this.makeKey(day, user.id));
                } else {
                    if (leave.users.find(u => u.id === user.id)) {
                        leave.users.splice(
                            leave.users.findIndex(u => u.id === user.id),
                            1
                        );

                        leaveDayUserMap.delete(this.makeKey(day, user.id));

                        if (leave.hourtype !== currentHourType) {
                            actionIsAdd = true;
                        }
                    } else {
                        leave.users.push(user);

                        actionIsAdd = true;
                    }
                }
            }

            if (!leaveData.includes(leave)) {
                leaveData.push(leave);
            }

            if (!leavesToSave.includes(leave)) {
                leavesToSave.push(leave);
            }

            if (!actionIsAdd) {
                if (callback) {
                    callback(structuredClone(leavesToSave));
                }
            }

            this.leaveToMap(leaveDayUserMap, leave, users);

            return actionIsAdd;
        };

        if (!leave) {
            if (!currentHourType) {
                return;
            }

            function createLeave(data: { comment: string; project: Project }) {
                if (data) {
                    contextProject = data.project;

                    leave = new Leave();

                    leave.hourtype = currentHourType;
                    leave.close_period = true;

                    if (leave.hourtype === RealisationHourtype.sleep) {
                        leave.afas_project_id = contextProject?.afas_project_id;
                    }

                    const beginDate = new Date(day);

                    Utils.setTime(beginDate, Settings.DEFAULT_START, 0, 0);

                    const endDate = new Date(beginDate);

                    Utils.setTime(endDate, Settings.DEFAULT_START + Settings.DEFAULT_DURATION_SLEEP, 0, 0);

                    leave.begindate = beginDate;
                    leave.enddate = endDate;

                    const begintime = new Date();

                    Utils.setTime(begintime, Settings.DEFAULT_START, 0, 0);

                    const endtime = new Date();

                    Utils.setTime(endtime, Settings.DEFAULT_START + Settings.DEFAULT_DURATION_SLEEP, 0, 0);

                    leave.comment = data.comment;
                    leave.begintime = formatDate(begintime, 'HH:mm', 'nl');
                    leave.endtime = formatDate(endtime, 'HH:mm', 'nl');

                    leaveUsers();

                    if (callback) {
                        callback(structuredClone(leavesToSave));
                    }
                }
            }

            if (addComment) {
                this.subscriptions.add(
                    this.dialog
                        .open<AddLeaveNameDialogComponent, any, returnData>(AddLeaveNameDialogComponent, {
                            width: '320px',
                            panelClass: 'comment-edit-dialog',
                            data: {
                                currentHourType,
                                contextComment,
                                contextProject,
                                projects: projectDaysList.map(pd => pd.project),
                            },
                        })
                        .afterClosed()
                        .subscribe((data: { comment: string; project: Project }) => {
                            createLeave(data);
                        })
                );
            } else {
                createLeave({ comment: contextComment, project: contextProject });
            }
        } else {
            if (leaveUsers() && setHourType) {
                this.addOrRemoveLeave(
                    user,
                    day,
                    leaveData,
                    leavesToSave,
                    leaveDayUserMap,
                    users,
                    projectDaysList,
                    currentHourType,
                    contextProject,
                    contextComment,
                    false,
                    false,
                    addComment,
                    callback
                );
            }
        }
    }

    leaveToMap(leaveDayUserMap: Map<string, Leave>, leave: Leave, users: User[]) {
        const leaveDates = this.leaveService.generateLeaveDates(leave);
        const userIds = leave.users.map(u => u.id);

        users
            .filter(u => (leave.common ? true : userIds.includes(u.id)))
            .forEach(user => {
                leaveDates.forEach(dates => {
                    const dateString = formatDate(dates.beginDate, 'yyyy-MM-dd', 'nl');

                    if (
                        !leave.excluded.find(
                            e => e.user_id === user.id && formatDate(e.date, 'yyyy-MM-dd', 'nl') === dateString
                        )
                    ) {
                        leaveDayUserMap.set(this.makeKey(dates.beginDate, user.id), leave);
                    }
                });
            });
    }

    makeKey(date: Date, id: number) {
        return `${formatDate(date, 'yyyy-MM-dd', 'nl')}-${id}`;
    }

    hasAsphaltFunction(employee: User) {
        return AuthorisationService.hasAnyRole(
            [this.environment.roles.CompactMachinist, this.environment.roles.Asphalt],
            employee
        );
    }

    hasCaoBouwInfra(employee: User) {
        return employee.group !== 'CHAU';
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
}
